import { useState } from 'react';
import { PhotoPleinEcran } from '../components/PhotoPleinEcran.jsx';
import { tr } from '../lib/i18n.js';
import { C } from '../theme/palette.js';

/** Photos par rangs de deux : à trois, elles restaient trop petites. */
const PAR_RANG = 2;

/**
 * Hauteur d'une photo, en pixels : environ 6,5 cm imprimés, trois rangs — six
 * photos — sur la page A4 de l'annexe, sous son en-tête. À 255, le troisième
 * rang manquait de quelques millimètres et passait seul à la page suivante.
 */
const HAUTEUR_PHOTO = 222;

/**
 * Annexe des photos et plans des lignes, à la manière de la planche photos de
 * l'écran : deux par rang, grandes, chacune avec sa désignation et sa nature.
 *
 * @param {object} props propriétés
 * @param {Array<{src: string, legende: string, nature: string, lien?: string}>} props.photos
 *   images ; avec un lien, la photo touchée dans le PDF s'ouvre en grand
 * @param {object} props.soc société
 * @param {string} props.titre titre du document
 * @param {boolean} [props.agrandit] dans l'application : une photo touchée
 *   s'ouvre en grand (le PDF, lui, a ses liens)
 * @param {boolean} [props.suite] les photos suivent le QR-facture sur la même
 *   page, sans saut de page ni en-tête répété, pour que l'offre tienne en deux
 *   pages quand c'est possible
 * @returns {JSX.Element|null} la planche, ou rien sans photo
 */
export function AnnexePhotos({ photos, soc, titre, suite = false, agrandit = false }) {
  const [enGrand, setEnGrand] = useState(null);
  const liste = (photos || []).filter((p) => p && p.src);
  if (!liste.length) return null;
  const ouvre = agrandit ? setEnGrand : undefined;
  return (
    <div
      className={suite ? 'rendu-annexe suite' : 'rendu-annexe'}
      style={{ background: '#fff', padding: suite ? '4px 18px 6px' : '15px 18px' }}
    >
      {!suite && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            borderBottom: '2px solid ' + C.navy,
            paddingBottom: 9,
            marginBottom: 14,
          }}
        >
          <div>
            {soc?.logo ? (
              <img src={soc.logo} alt="" style={{ height: 40, objectFit: 'contain' }} />
            ) : (
              <div style={{ fontSize: 15, fontWeight: 900, color: C.navy }}>{soc?.nom || ''}</div>
            )}
          </div>
          <div style={{ textAlign: 'right', fontSize: 8.5, color: C.grey, lineHeight: 1.5 }}>
            {soc?.nom}
            <br />
            {soc?.adr}
            <br />
            {soc?.npa}
          </div>
        </div>
      )}
      {suite ? (
        <PhotosSousLeBulletin liste={liste} ouvre={ouvre} />
      ) : (
        <>
          <div style={{ fontSize: 13, fontWeight: 800, color: C.navy, marginBottom: 12 }}>
            {tr('Annexe photos')}
            {titre ? ' — ' + titre : ''}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 11 }}>
            {liste.map((p, i) => (
              <Carte
                key={i}
                photo={p}
                largeur="calc(50% - 6px)"
                hauteur={HAUTEUR_PHOTO}
                ouvre={ouvre}
              />
            ))}
          </div>
        </>
      )}
      {enGrand && (
        <PhotoPleinEcran
          src={enGrand.src}
          legende={legende(enGrand)}
          onFerme={() => setEnGrand(null)}
        />
      )}
    </div>
  );
}

/**
 * Une photo et sa légende. Touchée dans le PDF, elle s'ouvre en grand : la
 * carte entière mène à la photo, en taille réelle, sur la page en ligne.
 */
function Carte({ photo, largeur, hauteur, ouvre }) {
  const carte = (
    <div
      className="ph-card"
      style={{
        // Dans son lien, la carte en prend toute la largeur.
        width: photo.lien || ouvre ? '100%' : largeur,
        border: '1px solid ' + C.border,
        borderRadius: 8,
        overflow: 'hidden',
        background: '#fafafa',
      }}
    >
      <img
        className="ph-img"
        src={photo.src}
        alt=""
        style={{ width: '100%', height: hauteur, objectFit: 'cover', display: 'block' }}
      />
      {/* Une seule ligne : une légende sur deux lignes repoussait le rang suivant. */}
      <div
        style={{
          padding: '5px 8px',
          fontSize: 8,
          color: C.grey,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {legende(photo)}
      </div>
    </div>
  );
  // Dans l'application, la photo touchée s'ouvre en grand.
  if (ouvre && !photo.lien) {
    return (
      <div
        role="button"
        tabIndex={0}
        aria-label={tr('Voir la photo en grand')}
        onClick={() => ouvre(photo)}
        style={{ width: largeur, cursor: 'zoom-in' }}
      >
        {carte}
      </div>
    );
  }
  if (!photo.lien) return carte;
  return (
    <a
      className="ph-lien"
      href={photo.lien}
      style={{ width: largeur, display: 'block', color: 'inherit', textDecoration: 'none' }}
    >
      {carte}
    </a>
  );
}

/**
 * La légende d'une photo : sa note, puis sa nature — sauf quand la note la dit
 * déjà (« Photo après · Photo »).
 */
function legende(photo) {
  const note = String(photo.legende || '');
  const nature = photo.nature ? tr(photo.nature) : '';
  const redite = nature && note.toLowerCase().startsWith(nature.toLowerCase());
  return [note, redite ? '' : nature].filter(Boolean).join(' · ');
}

/**
 * Les photos sous le QR-facture : deux par rang, grandes, pour qu'on y voie
 * le travail (« il faut que ce soit beaucoup plus grand »). Quand le premier rang ne tient pas sous le bulletin, il part sur la
 * page suivante avec le titre : le titre ne reste jamais seul en bas de page.
 */
function PhotosSousLeBulletin({ liste, ouvre }) {
  const rangs = [];
  for (let i = 0; i < liste.length; i += PAR_RANG) rangs.push(liste.slice(i, i + PAR_RANG));
  const rang = (photos, cle) => (
    <div key={cle} style={{ display: 'flex', gap: 10, marginBottom: 6 }}>
      {photos.map((p, i) => (
        <Carte key={i} photo={p} largeur="calc(50% - 5px)" hauteur={HAUTEUR_PHOTO} ouvre={ouvre} />
      ))}
    </div>
  );
  return (
    <>
      <div className="ph-groupe" style={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}>
        <div style={{ fontSize: 11, fontWeight: 800, color: C.navy, marginBottom: 4 }}>
          {tr('Photos')}
        </div>
        {rang(rangs[0], 0)}
      </div>
      {rangs.slice(1).map((r, i) => (
        <div
          key={i + 1}
          className="ph-groupe"
          style={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}
        >
          {rang(r, i + 1)}
        </div>
      ))}
    </>
  );
}
